import {view, css, Icon, Link} from '@sail/ui/deprecated';
import {support} from '@sail/icons/react/Icon';
import {document, api, list} from '@sail/icons/react/Icon__xsmall';
import {useAnalytics} from '@stripe-internal/inline-data';
import {IconAsset} from '@sail/icons/types';
import {useNewUniversalHelpDesign} from '@stripe-internal/conversations-ui';
import {LiveAnswersSource} from '../UniversalHelpLiveAnswersResponse';
import {sourcesLinkClickedActionEvent} from '../analytics';

type SourceLinkPrefix = {
  icon: IconAsset;
  iconColor: string;
  text?: string;
  textColor?: string;
};

const collectionToPrefix = new Map<string, SourceLinkPrefix>(
  Object.entries({
    support_docs: {
      icon: support,
      iconColor: '$color.brand500',
    },
    express_support_docs: {
      icon: support,
      iconColor: '$color.brand500',
    },
    support_topic_pages: {
      icon: list,
      iconColor: '$color.info300',
      text: 'Topic: ',
      textColor: '$color.info600',
    },
    link_support_docs: {
      icon: support,
      iconColor: '#00C767', // Link brand300
    },
    stripe_docs: {
      icon: document,
      iconColor: '$hue.blue500',
      text: 'Docs: ',
      textColor: '$hue.blue600',
    },
    stripe_api_docs: {
      icon: api,
      iconColor: '$color.brand500',
      text: 'API: ',
      textColor: '$color.brand500',
    },
  }),
);

// Used if we can't find a match for the collection or a collection isn't returned by the backend.
const defaultPrefix = {
  icon: support,
  iconColor: '$color.brand500',
};

export const SourceLink = ({source}: {source: LiveAnswersSource}) => {
  const analytics = useAnalytics();
  const useNewDesign = useNewUniversalHelpDesign();

  let prefix: SourceLinkPrefix = source.collection
    ? collectionToPrefix.get(source.collection) || defaultPrefix
    : defaultPrefix;

  if (useNewDesign && source.collection === 'stripe_docs') {
    prefix = {
      icon: document,
      text: 'Docs: ',
      // These are hardcoded because they come from stable Sail, but
      // this currently used deprecated Sail and to upgrade we'd need
      // to map all of these colors to new ones
      iconColor: '#533afd',
      textColor: '#533afd',
    };
  }

  return (
    <view.div
      css={{
        stack: 'x',
        alignY: 'center',
        alignX: 'start',
        gap: 'small',
      }}
    >
      <Icon
        icon={prefix.icon}
        size="xsmall"
        css={{
          color: css.value(prefix.iconColor),
          minWidth: css.value('12px'),
        }}
      />
      <Link
        href={source.url}
        target={source.url}
        onPress={() => {
          analytics.action(sourcesLinkClickedActionEvent, {
            live_answers_source_url: source.url,
            live_answers_source_title: source.title,
            live_answers_source_collection: source.collection,
            live_answers_source_score: source.score,
          });
        }}
        css={{
          fontFamily: css.value('label.medium'),
          fontSize: css.value('12px'),
          color: 'primary',
          fontWeight: css.value('600'),
          overflow: 'hidden',
          whiteSpace: css.value('nowrap'),
          textOverflow: css.value('ellipsis'),
        }}
      >
        <view.span css={{color: css.value(prefix.textColor || 'primary')}}>
          {prefix.text}
        </view.span>
        {source.title}
      </Link>
    </view.div>
  );
};
